// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { ProjectAssetAssignmentCard } from '@/project/components/project-asset-assignment-card';
import { ProjectLineItemsCard } from '@/project/components/project-line-items-card';
import { useProjectContext } from '@/project/hooks/use-project-context';
import { Box, Stack } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback } from 'react';
import { z } from 'zod';

const projectIndexSearchSchema = z.object({
  lineItemKey: z.string().optional().catch(undefined),
  // Deep-link target for the billing plan drawer. When set, the matching
  // charge row inside the drawer scrolls into view and briefly highlights
  // once the charge schedule loads.
  focusChargeKey: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/projects/$projectKey/')({
  component: ProjectIndexRoute,
  validateSearch: projectIndexSearchSchema,
});

function ProjectIndexRoute() {
  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <Suspense>
        <ProjectIndexContent />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function ProjectIndexContent() {
  const { project } = useProjectContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleSelectLineItem = useCallback(
    (lineItemKey: string | null) => {
      // Closing or switching the drawer also drops the deep-link focus charge
      // so it does not bleed into another line item's billing plan.
      void navigate({
        search: lineItemKey ? { lineItemKey } : {},
        replace: true,
      });
    },
    [navigate]
  );

  // Drops focusChargeKey from the URL after the drawer's charge list has had
  // a chance to scroll/highlight, so a refresh does not re-trigger it.
  const handleClearFocusCharge = useCallback(() => {
    if (search.focusChargeKey === undefined) {
      return;
    }
    void navigate({
      search: search.lineItemKey ? { lineItemKey: search.lineItemKey } : {},
      replace: true,
    });
  }, [navigate, search.focusChargeKey, search.lineItemKey]);

  return (
    <HardfinTabCardWrapper title="Line items">
      <Stack spacing={2} paddingTop={2}>
        <Box>
          <ProjectLineItemsCard
            project={project}
            activeLineItemKey={search.lineItemKey}
            focusChargeKey={search.focusChargeKey}
            onSelectLineItem={handleSelectLineItem}
            onFocusChargeComplete={handleClearFocusCharge}
          />
        </Box>
        <Box>
          <ProjectAssetAssignmentCard project={project} />
        </Box>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
